// Разбивката „с номер на бон / без номер" — БЕЗ ДУМИ.
//
// Запълнено кръгче срещу кръгче с контур, и двете в ФИРМЕНИЯ оранжев тон:
// същият знак, който носи и редът в справката. Категорията не се назовава
// никъде на екрана — знакът е целият надпис.
//
// Показва се САМО при сесия с втори код и само когато има какво да се раздели.
// Без втори код BE-то изобщо не връща сметките без номер, тоест числото би било
// нула по конструкция, а нула се чете като „няма такива" — по-подвеждащо от
// липсващ показател.
//
// С `onToggle` втората група става превключвател: кликът изолира точно тези
// сметки в списъка. Това е отговорът на въпроса „виждам, че са повече — кои са
// в повечето".
//
// Подадат ли се и СУМИТЕ (`fiscalizedAmount` / `unfiscalizedAmount`), всяка
// застава до своя брой в СЪЩАТА група, след тънък разделител: „● 10 | 480.00 €".
// Знакът не се повтаря — един знак, две числа. Двете групи са в `flex-wrap`:
// има ли място, стоят на един ред; няма ли — втората пада отдолу.

import { useTranslation } from "react-i18next";

import { fmtEuro } from "../../../utils/currency";

type Props = {
  fiscalized: number;
  unfiscalized: number;
  /** Сумите зад двата броя; и двете или нито една. Decimal → JSON низ. */
  fiscalizedAmount?: string;
  unfiscalizedAmount?: string;
  /** Липсва → само показва. Има ли го → втората група е превключвател. */
  onToggle?: () => void;
  /** Изолирани ли са в момента (само при `onToggle`). */
  isolated?: boolean;
};

const DOT = "size-[9px] shrink-0 rounded-full";
const DOT_FILLED = `${DOT} bg-brand`;
const DOT_OUTLINED = `${DOT} border-[1.5px] border-brand bg-transparent`;

/** „брой | сума" — сумата е незадължителна; без нея остава само броят. */
const CountWithAmount = ({
  count,
  amount,
}: {
  count: number;
  amount?: string;
}) => (
  <span className="flex items-center gap-1.5 whitespace-nowrap font-pos-mono tabular-nums">
    <span>{count}</span>
    {amount !== undefined ? (
      <>
        <span aria-hidden className="text-shade-5">
          |
        </span>
        <span>{fmtEuro(amount)}</span>
      </>
    ) : null}
  </span>
);

export const FiscalSplit = ({
  fiscalized,
  unfiscalized,
  fiscalizedAmount,
  unfiscalizedAmount,
  onToggle,
  isolated = false,
}: Props) => {
  const { t } = useTranslation();

  const marked = (
    <>
      <span aria-hidden className={DOT_OUTLINED} />
      <CountWithAmount count={unfiscalized} amount={unfiscalizedAmount} />
    </>
  );

  return (
    <span className="flex flex-wrap items-center gap-x-2.5 gap-y-0.5">
      <span className="flex items-center gap-1.5">
        <span aria-hidden className={DOT_FILLED} />
        <CountWithAmount count={fiscalized} amount={fiscalizedAmount} />
      </span>

      {onToggle ? (
        <button
          type="button"
          onClick={(event) => {
            // Кутийката е кликаема цялата (води към списъка) — този клик е
            // ДРУГО действие и не бива да минава и през нея.
            event.stopPropagation();
            onToggle();
          }}
          aria-pressed={isolated}
          // Името за екранни четци описва ЗНАКА, не категорията: думата за нея
          // не съществува в интерфейса.
          aria-label={t("reportsPage.billsHistoryIsolateMarked")}
          title={t("reportsPage.billsHistoryIsolateMarked")}
          className={`flex items-center gap-1.5 rounded-md px-1.5 py-0.5 transition-colors ${
            isolated ? "bg-brand-tint text-brand-deep-ink" : "hover:bg-shade-1"
          }`}
        >
          {marked}
        </button>
      ) : (
        <span className="flex items-center gap-1.5">{marked}</span>
      )}
    </span>
  );
};
